import type { MessageOption } from '../../api/types'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'
import { MessageText } from './MessageText'

interface Props {
  /** The checklist's words, which name the group of items. */
  body: string
  items: MessageOption[]
  /** Ids for the page's own title, words and progress line. */
  idPrefix: string
  ticked: (id: string) => boolean
  /** Once finished: why each item not ticked was not done, by item id. */
  reasons: Map<string, string>
  /** "3 of 30 ticked · Answer by 02:46", or how it ended. */
  caption: string
  /** Still open: the rows tick and Done shows. */
  open: boolean
  /** A tick is being saved: Done waits for it. */
  busy: boolean
  onToggle: (item: MessageOption) => void
  onDone: () => void
  onClose: () => void
  /** The text field to give focus back to as the page closes, so its keyboard comes back. */
  focusOnClose: HTMLElement | null
}

/**
 * A checklist's own page, opened from its bubble: the words, the rows to tick, and Done in the top bar. The progress line stays
 * under the bar while the rows scroll. Nothing here takes text, so the keyboard goes down while it is open; it comes back as the
 * page closes when the message box had it (`focusOnClose`).
 */
export function ChecklistPage({ body, items, idPrefix, ticked, reasons, caption, open, busy, onToggle, onDone, onClose, focusOnClose }: Props) {
  const wordsId = `${idPrefix}-words`
  const captionId = `${idPrefix}-progress`
  return (
    <Dialog
      chrome="bar"
      open
      onClose={onClose}
      title="Checklist"
      titleId={`${idPrefix}-title`}
      closeLabel="Close"
      // Nothing here takes text, so it can slide up like a phone's own screens (responsive.css).
      className="checklist-page slides-up"
      focusOnClose={focusOnClose}
      action={
        open ? (
          <button type="button" className="bar-button bar-action" disabled={busy} onClick={onDone}>
            Done
          </button>
        ) : null
      }
    >
      <p id={captionId} className="checklist-progress">
        {caption}
      </p>
      <div className="dialog-body checklist-page-body">
        <p id={wordsId} className="checklist-words">
          <MessageText body={body} meUsername={null} />
        </p>
        <div role="group" aria-labelledby={wordsId} aria-describedby={captionId} className="checklist-items">
          {items.map((item) => (
            <label key={item.id} className="checklist-item">
              <input
                type="checkbox"
                checked={ticked(item.id)}
                disabled={!open}
                onChange={() => onToggle(item)}
                // Named by the item; a reason it was not done describes it.
                aria-labelledby={`${idPrefix}-item-${item.id}`}
                aria-describedby={reasons.has(item.id) ? `${idPrefix}-reason-${item.id}` : undefined}
              />
              <span className="checklist-box" aria-hidden="true">
                {ticked(item.id) ? <Icon name="check" /> : null}
              </span>
              <span className="checklist-label">
                <span id={`${idPrefix}-item-${item.id}`}>{item.label}</span>
                {reasons.has(item.id) ? (
                  <small id={`${idPrefix}-reason-${item.id}`} className="checklist-reason">
                    {reasons.get(item.id)}
                  </small>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      </div>
    </Dialog>
  )
}
